import React, { useEffect, useMemo, useState } from 'react';
import { PlusIcon, SearchIcon, PencilIcon, Trash2Icon, ArrowUpIcon, ArrowDownIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Badge } from '../components/ui/Badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/Table';
import { Modal } from '../components/ui/Modal';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { StatusCode, SortConfig } from '../types/catalog';
import { generateMockStatusCodes } from '../utils/catalogData';
export function StatusCatalog() {
  // Data State
  const [statusCodes, setStatusCodes] = useState<StatusCode[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  // Filter & Sort State
  const [search, setSearch] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    key: 'statusCode',
    direction: 'asc'
  });
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState<StatusCode | null>(null);
  // Form State
  const [formData, setFormData] = useState({
    statusCode: '',
    statusName: '',
    description: '',
    color: 'green' as 'green' | 'red' | 'yellow' | 'blue'
  });
  const [formErrors, setFormErrors] = useState({
    statusCode: false,
    statusName: false
  });
  // Delete Confirm State
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [statusToDelete, setStatusToDelete] = useState<StatusCode | null>(null);
  // Load initial data
  useEffect(() => {
    setStatusCodes(generateMockStatusCodes());
    setIsLoaded(true);
  }, []);
  // Derived Data (Filtering & Sorting)
  const filteredAndSortedStatuses = useMemo(() => {
    let result = [...statusCodes];
    // Apply Search
    if (search) {
      const searchLower = search.toLowerCase();
      result = result.filter(status => status.statusCode.toString().includes(searchLower) || status.statusName.toLowerCase().includes(searchLower) || status.description.toLowerCase().includes(searchLower));
    }
    // Apply Sorting
    result.sort((a, b) => {
      const aValue = a[sortConfig.key as keyof StatusCode];
      const bValue = b[sortConfig.key as keyof StatusCode];
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [statusCodes, search, sortConfig]);
  // Pagination calculations
  const totalPages = Math.ceil(filteredAndSortedStatuses.length / pageSize);
  const paginatedStatuses = filteredAndSortedStatuses.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, pageSize]);
  // Handlers
  const handleSort = (key: string) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };
  const openAddModal = () => {
    setEditingStatus(null);
    setFormData({
      statusCode: '',
      statusName: '',
      description: '',
      color: 'green'
    });
    setFormErrors({
      statusCode: false,
      statusName: false
    });
    setIsModalOpen(true);
  };
  const openEditModal = (status: StatusCode) => {
    setEditingStatus(status);
    setFormData({
      statusCode: status.statusCode,
      statusName: status.statusName,
      description: status.description,
      color: status.color
    });
    setFormErrors({
      statusCode: false,
      statusName: false
    });
    setIsModalOpen(true);
  };
  const handleSaveStatus = () => {
    // Validate
    const errors = {
      statusCode: !formData.statusCode.trim(),
      statusName: !formData.statusName.trim()
    };
    setFormErrors(errors);
    if (errors.statusCode || errors.statusName) return;
    if (editingStatus) {
      // Update
      setStatusCodes(prev => prev.map(s => s.id === editingStatus.id ? {
        ...s,
        ...formData,
        statusCode: formData.statusCode
      } : s));
    } else {
      // Add new
      const newStatus: StatusCode = {
        id: `sc-${Date.now()}`,
        statusCode: formData.statusCode,
        statusName: formData.statusName,
        description: formData.description,
        color: formData.color,
        createdAt: new Date().toISOString()
      };
      setStatusCodes(prev => [...prev, newStatus]);
    }
    setIsModalOpen(false);
  };
  const confirmDelete = (status: StatusCode) => {
    setStatusToDelete(status);
    setIsConfirmOpen(true);
  };
  const handleDelete = () => {
    if (statusToDelete) {
      setStatusCodes(prev => prev.filter(s => s.id !== statusToDelete.id));
    }
    setIsConfirmOpen(false);
    setStatusToDelete(null);
  };
  const formatDate = (isoString: string) => {
    return new Date(isoString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  const getColorBadge = (color: string, label: string) => {
    switch (color) {
      case 'green':
        return <Badge variant="success" data-id="element-337">{label}</Badge>;
      case 'red':
        return <Badge variant="destructive" data-id="element-338">{label}</Badge>;
      case 'yellow':
        return <Badge variant="warning" data-id="element-339">{label}</Badge>;
      case 'blue':
        return <span className="inline-flex items-center rounded-full border border-transparent bg-blue-500/15 px-2.5 py-0.5 text-xs font-semibold text-blue-500" data-id="element-340">
            {label}
          </span>;
      default:
        return <Badge variant="secondary" data-id="element-341">{label}</Badge>;
    }
  };
  const SortIcon = ({
    columnKey
  }: {
    columnKey: string;
  }) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === 'asc' ? <ArrowUpIcon className="w-3 h-3 ml-1" data-id="element-342" /> : <ArrowDownIcon className="w-3 h-3 ml-1" data-id="element-343" />;
  };
  if (!isLoaded) return null;
  return <div className="space-y-6" data-id="element-344">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-345">
        <div data-id="element-346">
          <h2 className="text-2xl font-bold tracking-tight" data-id="element-347">
            Catálogo de Códigos de Estado
          </h2>
          <p className="text-muted-foreground mt-1" data-id="element-348">
            Administra las definiciones de códigos de estado
          </p>
        </div>
        <Button onClick={openAddModal} data-id="element-349">
          <PlusIcon className="w-4 h-4 mr-2" data-id="element-350" />
          Agregar Estado
        </Button>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row justify-between gap-4 bg-white p-4 rounded-lg border border-gray-200 shadow-sm" data-id="element-351">
        <div className="relative w-full sm:w-96" data-id="element-352">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" data-id="element-353" />
          <Input placeholder="Buscar por código, nombre o descripción..." className="pl-9 bg-background" value={search} onChange={e => setSearch(e.target.value)} data-id="element-354" />
        </div>
        <div className="flex items-center text-sm text-muted-foreground" data-id="element-355">
          {filteredAndSortedStatuses.length} códigos en total
        </div>
      </div>

      {/* Table */}
      <div className="rounded-md border border-gray-200 bg-white overflow-hidden shadow-sm" data-id="element-356">
        <Table data-id="element-357">
          <TableHeader className="bg-gray-50" data-id="element-358">
            <TableRow data-id="element-359">
              <TableHead className="cursor-pointer hover:text-foreground transition-colors w-[120px]" onClick={() => handleSort('statusCode')} data-id="element-360">
                <div className="flex items-center" data-id="element-361">
                  Código <SortIcon columnKey="statusCode" data-id="element-362" />
                </div>
              </TableHead>
              <TableHead className="cursor-pointer hover:text-foreground transition-colors w-[200px]" onClick={() => handleSort('statusName')} data-id="element-363">
                <div className="flex items-center" data-id="element-364">
                  Nombre del Estado <SortIcon columnKey="statusName" data-id="element-365" />
                </div>
              </TableHead>
              <TableHead data-id="element-366">Descripción</TableHead>
              <TableHead className="cursor-pointer hover:text-foreground transition-colors w-[150px]" onClick={() => handleSort('color')} data-id="element-367">
                <div className="flex items-center" data-id="element-368">
                  Indicador <SortIcon columnKey="color" data-id="element-369" />
                </div>
              </TableHead>
              <TableHead className="cursor-pointer hover:text-foreground transition-colors w-[150px]" onClick={() => handleSort('createdAt')} data-id="element-370">
                <div className="flex items-center" data-id="element-371">
                  Fecha de Creación <SortIcon columnKey="createdAt" data-id="element-372" />
                </div>
              </TableHead>
              <TableHead className="text-right w-[100px]" data-id="element-373">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-id="element-374">
            {paginatedStatuses.length === 0 ? <TableRow data-id="element-375">
                <TableCell colSpan={6} className="h-32 text-center text-muted-foreground" data-id="element-376">
                  No se encontraron códigos de estado con los criterios
                  indicados.
                </TableCell>
              </TableRow> : paginatedStatuses.map(status => <TableRow key={status.id} data-id="element-377">
                  <TableCell className="font-mono font-bold text-foreground" data-id="element-378">
                    {status.statusCode}
                  </TableCell>
                  <TableCell className="font-medium text-foreground" data-id="element-379">
                    {status.statusName}
                  </TableCell>
                  <TableCell className="text-muted-foreground max-w-[300px] truncate" title={status.description} data-id="element-380">
                    {status.description || '-'}
                  </TableCell>
                  <TableCell data-id="element-381">
                    <div className="flex items-center space-x-2" data-id="element-382">
                      <div className={`w-2 h-2 rounded-full ${status.color === 'green' ? 'bg-green-500' : status.color === 'red' ? 'bg-red-500' : status.color === 'yellow' ? 'bg-yellow-500' : 'bg-blue-500'}`} data-id="element-383" />
                      {getColorBadge(status.color, status.color.charAt(0).toUpperCase() + status.color.slice(1))}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm" data-id="element-384">
                    {formatDate(status.createdAt)}
                  </TableCell>
                  <TableCell className="text-right" data-id="element-385">
                    <div className="flex justify-end space-x-1" data-id="element-386">
                      <Button variant="ghost" size="icon" onClick={() => openEditModal(status)} title="Editar" data-id="element-387">
                        <PencilIcon className="w-4 h-4 text-muted-foreground hover:text-foreground" data-id="element-388" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => confirmDelete(status)} title="Eliminar" data-id="element-389">
                        <Trash2Icon className="w-4 h-4 text-destructive/70 hover:text-destructive" data-id="element-390" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>)}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground" data-id="element-391">
        <div className="flex items-center space-x-2" data-id="element-392">
          <span data-id="element-393">Mostrar</span>
          <Select className="w-20 h-8" value={pageSize.toString()} onChange={e => setPageSize(Number(e.target.value))} data-id="element-394">
            <option value="10" data-id="element-395">10</option>
            <option value="25" data-id="element-396">25</option>
            <option value="50" data-id="element-397">50</option>
          </Select>
          <span data-id="element-398">por página</span>
        </div>

        <div data-id="element-399">
          Mostrando{' '}
          {filteredAndSortedStatuses.length === 0 ? 0 : (currentPage - 1) * pageSize + 1}{' '}
          a {Math.min(currentPage * pageSize, filteredAndSortedStatuses.length)}{' '}
          de {filteredAndSortedStatuses.length} resultados
        </div>

        <div className="flex items-center space-x-2" data-id="element-400">
          <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} data-id="element-401">
            Anterior
          </Button>
          <span className="px-2" data-id="element-402">
            Página {currentPage} de {Math.max(1, totalPages)}
          </span>
          <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || totalPages === 0} data-id="element-403">
            Siguiente
          </Button>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingStatus ? 'Editar Código de Estado' : 'Agregar Nuevo Código de Estado'} description={editingStatus ? 'Actualiza la definición del código de estado.' : 'Crea un nuevo código de estado para el catálogo.'} data-id="element-404">
        <div className="space-y-4" data-id="element-405">
          <div className="grid grid-cols-2 gap-4" data-id="element-406">
            <div className="space-y-2" data-id="element-407">
              <label className="text-sm font-medium" data-id="element-408">
                Código de Estado <span className="text-destructive" data-id="element-409">*</span>
              </label>
              <Input type="text" placeholder="ej. SC-200" value={formData.statusCode} onChange={e => setFormData(prev => ({
              ...prev,
              statusCode: e.target.value
            }))} className={formErrors.statusCode ? 'border-destructive' : ''} data-id="element-410" />
              {formErrors.statusCode && <p className="text-xs text-destructive" data-id="element-411">
                  El código de estado es requerido
                </p>}
            </div>
            <div className="space-y-2" data-id="element-412">
              <label className="text-sm font-medium" data-id="element-413">Indicador de Color</label>
              <div className="flex gap-2" data-id="element-414">
                {[{
                id: 'green',
                class: 'bg-green-500'
              }, {
                id: 'blue',
                class: 'bg-blue-500'
              }, {
                id: 'yellow',
                class: 'bg-yellow-500'
              }, {
                id: 'red',
                class: 'bg-red-500'
              }].map(color => <button key={color.id} onClick={() => setFormData(prev => ({
                ...prev,
                color: color.id as any
              }))} className={`w-10 h-10 rounded-md flex items-center justify-center transition-all ${formData.color === color.id ? 'ring-2 ring-offset-2 ring-offset-background ring-primary scale-110' : 'hover:scale-105 opacity-80 hover:opacity-100'}`} data-id="element-415">
                    <div className={`w-6 h-6 rounded-full ${color.class}`} data-id="element-416" />
                  </button>)}
              </div>
            </div>
          </div>

          <div className="space-y-2" data-id="element-417">
            <label className="text-sm font-medium" data-id="element-418">
              Nombre del Estado <span className="text-destructive" data-id="element-419">*</span>
            </label>
            <Input placeholder="ej. OK, No Encontrado" value={formData.statusName} onChange={e => setFormData(prev => ({
            ...prev,
            statusName: e.target.value
          }))} className={formErrors.statusName ? 'border-destructive' : ''} data-id="element-420" />
            {formErrors.statusName && <p className="text-xs text-destructive" data-id="element-421">
                El nombre del estado es requerido
              </p>}
          </div>

          <div className="space-y-2" data-id="element-422">
            <label className="text-sm font-medium" data-id="element-423">Descripción</label>
            <textarea className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50" placeholder="Explicación detallada de cuándo ocurre este estado..." value={formData.description} onChange={e => setFormData(prev => ({
            ...prev,
            description: e.target.value
          }))} data-id="element-424" />
          </div>

          <div className="flex justify-end space-x-3 pt-4 mt-4 border-t border-border/50" data-id="element-425">
            <Button variant="outline" onClick={() => setIsModalOpen(false)} data-id="element-426">
              Cancelar
            </Button>
            <Button onClick={handleSaveStatus} data-id="element-427">Guardar Estado</Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmDialog isOpen={isConfirmOpen} onClose={() => setIsConfirmOpen(false)} onConfirm={handleDelete} title="Eliminar Código de Estado" message={`¿Estás seguro de que deseas eliminar el código de estado ${statusToDelete?.statusCode} (${statusToDelete?.statusName})? Esta acción no se puede deshacer.`} confirmText="Eliminar Estado" cancelText="Cancelar" data-id="element-428" />
    </div>;
}